// frontend/src/utils/permissions.js
//
// Reads the capability list the backend puts on the user profile. There is
// deliberately NO copy of the role -> capability table here: `backend/
// permissions.py` is the single source of truth and serializes the result into
// `authUser.capabilities` on /api/auth/v1/google/verify-token and /auth/v1/me.
// Two copies of a permission model drift, and the copy that drifts is the one
// that decides what renders.
//
// So components ask `can(authUser, CAP.PURCHASE)` and never compare a role
// name. Adding a capability is a one-file change in Python.

export const CAP = {
    VIEW_ADMIN_SCREENS: 'viewAdminScreens',
    ADMIN_WRITE: 'adminWrite',
    MANAGE_USERS: 'manageUsers',
    SELECT_ANY_CUSTOMER: 'selectAnyCustomer',
    WRITE_CUSTOMER_PROFILE: 'writeCustomerProfile',
    PURCHASE: 'purchase',
    VIEW_INTERNAL_PRODUCTS: 'viewInternalProducts',
};

// Fires when the backend refuses a request in a way that suggests the caller's
// own permissions changed underneath them. App.js listens and re-fetches the
// profile, so a demoted user's UI stops offering actions that now 403.
export const PERMISSIONS_CHANGED_EVENT = 'auth-permissions-changed';

/**
 * True when this user holds `capability`.
 *
 * Fails closed on anything unexpected — a missing or malformed capability list
 * (an older backend, a half-deployed frontend) grants nothing rather than
 * everything.
 */
export const can = (authUser, capability) =>
    Array.isArray(authUser?.capabilities) && authUser.capabilities.includes(capability);

/** True when the user holds every capability listed. */
export const canAll = (authUser, capabilities = []) =>
    capabilities.every((capability) => can(authUser, capability));

/** True when the user holds at least one of the capabilities listed. */
export const canAny = (authUser, capabilities = []) =>
    capabilities.some((capability) => can(authUser, capability));

// Human labels for the roles. The four internal ones are what the user
// management page assigns; `customer` and `prospect` are derived from the token
// and never stored.
export const ROLE_LABELS = {
    superadmin: 'SuperAdmin',
    admin: 'Admin',
    user: 'User',
    readonly: 'Read only',
    customer: 'Customer',
    prospect: 'Prospect',
    // Superseded vocabulary. login_history rows written before the allowlist
    // existed still carry these, and an audit log is not rewritten to claim a
    // role that did not exist at that timestamp — so they must keep rendering.
    internal_admin: 'Internal admin',
    existing_customer: 'Customer',
    potential_customer: 'Prospect',
};

// Roles the user management page may assign, most privileged first. The four
// internal roles do nest today (Read only ⊂ User ⊂ Admin ⊂ SuperAdmin), so the
// order is a real ladder rather than just a convention — but the authoritative
// answer is always the capability list on the profile, never this position.
export const ASSIGNABLE_ROLES = ['superadmin', 'admin', 'user', 'readonly'];

// One-line explanation per assignable role, for the picker. Descriptive only —
// the authoritative capability list comes from /api/admin/users/roles.
export const ROLE_DESCRIPTIONS = {
    superadmin: 'Everything an Admin can do, plus managing these users.',
    admin: 'Full access to every admin screen and action, except this page.',
    user: 'Everything Read only can see, plus placing orders and editing profiles for any customer.',
    readonly: 'Browse any customer’s dashboard, profile and order history. No admin screens, and no changes.',
};

const ROLE_BADGE_CLASSES = {
    superadmin: 'bg-purple-100 text-purple-800 border border-purple-200',
    admin: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
    user: 'bg-blue-100 text-blue-800 border border-blue-200',
    readonly: 'bg-gray-100 text-gray-700 border border-gray-200',
    customer: 'bg-green-100 text-green-800 border border-green-200',
    prospect: 'bg-amber-100 text-amber-800 border border-amber-200',
    // Legacy values, coloured to match their nearest current equivalent so old
    // login_history rows stay readable at a glance.
    internal_admin: 'bg-purple-100 text-purple-800 border border-purple-200',
    existing_customer: 'bg-green-100 text-green-800 border border-green-200',
    potential_customer: 'bg-amber-100 text-amber-800 border border-amber-200',
};

/** Tailwind classes for a role pill. Unknown roles get a neutral pill. */
export const roleBadgeClass = (role) =>
    ROLE_BADGE_CLASSES[role] || 'bg-gray-100 text-gray-600 border border-gray-200';

/**
 * Display label for a role.
 *
 * Refused sign-ins are recorded with no role at all, so a missing value is
 * shown as a dash rather than as an empty pill.
 */
export const roleLabel = (role) => ROLE_LABELS[role] || role || '—';
